import { MobXProviderContext } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ParticipantModel } from 'Models/ParticipantModel';
import { useCallback, useContext, useEffect, useState } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { isNullOrUndefined } from 'util';
import { GroupMember } from './useGroupMemberContacts.types';

/**
 * Loads the contacts for participants in a conversation.
 * Directory members use the person's phone. SMS numbers use the participant phone,
 * which stays the number in the group even when a saved contact has a different primary number.
 *
 * @param conversationId - The ID of the conversation to load members for
 * @returns members and whether those contacts are still loading
 *
 * @example
 * ```tsx
 * const { members, loadingContacts } = useGroupMemberContacts('conversation-123');
 * ```
 */
export const useGroupMemberContacts = (conversationId: string) => {
  const {
    participant: { selectParticipantsByConversationId },
    contact: contactStore,
    person: personStore,
  } = useContext<RootStoreProps>(MobXProviderContext);

  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);

  const participantsByConversationId =
    selectParticipantsByConversationId(conversationId);

  // Derive current participants from the store on every render so the list
  // updates when the store changes. (MobX bs)
  const currentParticipants: ParticipantModel[] =
    participantsByConversationId?.state === 'fulfilled'
      ? participantsByConversationId.value.data.results
      : [];

  /**
   * Loads contact information for a given participant
   * Triggers loading in the store but doesn't store the result
   *
   * @param participant - The participant model to load contact for
   */
  const loadContact = useCallback(
    async (participant: ParticipantModel) => {
      if (participant.personId) {
        await personStore.loadPersonByIdGetIfMissingGet(
          participant.personId.toString()
        );
      } else if (participant.phone) {
        await personStore.getAllContactsWithSameNum(participant.phone);
        await contactStore.loadContactByPhoneNumber(participant.phone);
      }
    },
    [contactStore, personStore]
  );

  // Trigger contact loading when conversation or participant list changes
  useEffect(() => {
    const loadContactsForParticipants = async (
      participants: ParticipantModel[]
    ) => {
      setLoadingContacts(true);
      await Promise.all(participants.map((p) => loadContact(p)));
      setLoadingContacts(false);
    };

    if (!isNullOrUndefined(participantsByConversationId)) {
      participantsByConversationId.case({
        fulfilled: (resp) => {
          void loadContactsForParticipants(resp.data.results);
        },
        pending: () => {
          setLoadingContacts(true);
        },
      });
    }
  }, [conversationId, loadContact, participantsByConversationId]);

  // Get contacts reactively from the store using current participants.
  // Reading currentParticipants during render keeps the list in sync when
  // the store updates (e.g. after ConversationLeave).
  const getMembers = (): GroupMember[] => {
    const members: GroupMember[] = [];

    currentParticipants.forEach(({ personId, phone }) => {
      if (personId) {
        // Get reactive contact from person store
        const contact = personStore.selectPersonValueById(personId);
        if (contact) {
          members.push({
            contact,
            phoneNumber: contact.phoneNumber,
          });
        }
      } else if (phone) {
        // Try to get saved contacts with same number.
        // Opt-out is tracked against the number in the conversation.
        const contactsPbo = personStore.getAllContactsWithSameNum(phone);
        const savedContacts =
          contactsPbo?.state === 'fulfilled' ? contactsPbo.value : null;

        if (savedContacts && savedContacts.length) {
          members.push({
            contact: savedContacts[0],
            phoneNumber: phone,
          });
        } else {
          // Fall back to unsaved contact
          const contactPbo = contactStore.loadContactByPhoneNumber(phone);
          const unsavedContact =
            contactPbo?.state === 'fulfilled'
              ? (contactPbo.value as ContactModelBase)
              : null;

          if (unsavedContact) {
            members.push({
              contact: unsavedContact,
              phoneNumber: phone,
            });
          }
        }
      }
    });

    return members;
  };

  return {
    members: getMembers(),
    loadingContacts,
  };
};
